import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const ThemeToggle = ({ variant = 'icon', className = '' }) => {
  const { theme, isDark, toggleTheme, setLightTheme, setDarkTheme } = useTheme();

  // Simple icon toggle
  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className={`relative h-9 w-9 rounded-lg ${className}`}
        data-testid="theme-toggle"
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        <span className="sr-only">Toggle theme</span>
      </Button>
    );
  }

  // Dropdown with options
  if (variant === 'dropdown') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`relative h-9 w-9 rounded-lg ${className}`}
            data-testid="theme-toggle-dropdown"
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={setLightTheme} className="gap-2">
            <Sun className="h-4 w-4" />
            Light
            {theme === 'light' && <span className="ml-auto text-purple-600">✓</span>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={setDarkTheme} className="gap-2">
            <Moon className="h-4 w-4" />
            Dark
            {theme === 'dark' && <span className="ml-auto text-purple-600">✓</span>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Button with text
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleTheme}
      className={`gap-2 ${className}`}
      data-testid="theme-toggle-btn"
    >
      {isDark ? (
        <>
          <Sun className="h-4 w-4" />
          <span className="hidden sm:inline">Light</span>
        </>
      ) : (
        <>
          <Moon className="h-4 w-4" />
          <span className="hidden sm:inline">Dark</span>
        </>
      )}
    </Button>
  );
};

export default ThemeToggle;
